import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCircleArrowLeft } from "react-icons/fa6";
import { Handshake, Rocket, CreditCard, Shield, RefreshCw, MessageCircle } from 'lucide-react';

function TermsOfService() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const styles = `
        .tos-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%);
            padding: 2rem 1rem;
        }

        .tos-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border-radius: 24px;
            padding: 2.5rem;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
            border: 1px solid rgba(6, 182, 212, 0.15);
        }

        .tos-title {
            font-size: 2.5rem;
            font-weight: 800;
            background: linear-gradient(90deg, #6366f1, #06b6d4);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 0.5rem;
        }

        .last-updated {
            color: #666;
            font-size: 0.9rem;
            margin-bottom: 2rem;
        }

        .section-title {
            color: #0e4aa5;
            font-weight: 700;
            font-size: 1.3rem;
            margin: 1.5rem 0 1rem 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .section-icon {
            width: 32px;
            height: 32px;
            background: linear-gradient(135deg, #6366f1, #06b6d4);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
        }

        .tos-text {
            color: #444;
            line-height: 1.8;
            font-size: 1rem;
            margin-bottom: 1rem;
            text-align: left;
            font-weight: bold;
        }

        .tos-list {
            list-style: none;
            padding-left: 0;
            margin-left: 0;
            text-align: left;
        }

        .tos-list li {
            color: #555;
            line-height: 1.8;
            margin-bottom: 0.5rem;
            position: relative;
            padding-left: 1.8rem;
            text-align: left;
        }

        .tos-list li::before {
            content: "•";
            color: #06b6d4;
            font-weight: bold;
            position: absolute;
            left: 0.4rem;
        }

        @media (max-width: 768px) {
            .tos-card {
                padding: 1.5rem;
            }
            .tos-title {
                font-size: 2rem;
            }
            .section-title {
                font-size: 1.2rem;
            }
        }
    `;

    return (
        <div className="tos-container">
            <style>{styles}</style>
            <div className="container" style={{ maxWidth: 900 }}>
                <button
                    className="btn btn-outline-primary d-flex align-items-center mb-4"
                    onClick={() => navigate('/')}
                    style={{
                        borderRadius: '12px',
                        padding: '10px 20px',
                        borderWidth: '2px',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0d53bd';
                        e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#0d53bd';
                    }}
                >
                    <FaCircleArrowLeft className="me-2" size={18} />
                    Back to Home
                </button>

                <div className="tos-card">
                    <h1 className="tos-title">Terms of Service</h1>
                    <p className="last-updated">Last Updated: April 20, 2026</p>

                    <p className="tos-text">
                        These Terms of Service govern your use of InsightDraft AI's platform and services. 
                        By using our services, you enter into a binding agreement with us.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><Handshake size={18} /></span>
                        Service Agreement
                    </div>
                    <p className="tos-text">
                        This agreement is between you ("User," "you," or "your") and InsightDraft AI ("Company," 
                        "we," "us," or "our"). It covers all services provided through our website and platform.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><Rocket size={18} /></span>
                        Free Services
                    </div>
                    <p className="tos-text">
                        InsightDraft AI offers free business analysis services with the following conditions:
                    </p>
                    <ul className="tos-list">
                        <li>Free analyses are limited to reasonable usage (fair use policy applies)</li>
                        <li>We reserve the right to limit or modify free service offerings</li>
                        <li>AI-generated content may not be redistributed for commercial purposes</li>
                        <li>Free users may experience lower priority during peak times</li>
                    </ul>

                    <div className="section-title">
                        <span className="section-icon"><CreditCard size={18} /></span>
                        Paid Services
                    </div>
                    <p className="tos-text">
                        When you purchase paid services:
                    </p>
                    <ul className="tos-list">
                        <li>All fees are non-refundable unless required by law</li>
                        <li>Subscription renewals are automatic unless cancelled</li>
                        <li>We may change pricing with 30 days notice</li>
                        <li>Payment information is processed securely through third-party providers</li>
                    </ul>

                    <div className="section-title">
                        <span className="section-icon"><Shield size={18} /></span>
                        Acceptable Use Policy
                    </div>
                    <p className="tos-text">
                        You agree not to:
                    </p>
                    <ul className="tos-list">
                        <li>Use the service for illegal purposes</li>
                        <li>Attempt to reverse engineer or extract our AI models</li>
                        <li>Use automated scripts or bots without permission</li>
                        <li>Upload malicious code or content</li>
                        <li>Impersonate others or provide false information</li>
                        <li>Resell or redistribute our services without authorization</li>
                    </ul>

                    <div className="section-title">
                        <span className="section-icon"><RefreshCw size={18} /></span>
                        Service Availability
                    </div>
                    <p className="tos-text">
                        We strive to maintain 99.9% uptime but do not guarantee uninterrupted service. 
                        We may perform maintenance that temporarily affects availability. We will make 
                        reasonable efforts to notify users of planned downtime.
                    </p>

                    <div className="section-title">
                        <span className="section-icon"><MessageCircle size={18} /></span>
                        Support Services
                    </div>
                    <p className="tos-text">
                        We provide support through:
                    </p>
                    <ul className="tos-list">
                        <li>Email support: c84289798@gmail.com</li>
                        <li>Response time: Within 24-48 hours for free users, 12 hours for paid users</li>
                        <li>Support available in English</li>
                    </ul>

                    {/* <div className="section-title">
                        <span className="section-icon"><Handshake size={18} /></span>
                        Governing Law
                    </div>
                    <p className="tos-text">
                        These Terms of Service are governed by the laws of India. Any disputes shall be 
                        resolved in the courts of Ahmedabad, Gujarat, India.
                    </p> */}
                </div>
            </div>
        </div>
    );
}

export default TermsOfService;